/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type {
    FormArray,
    AbstractControl,
    FormControl,
    FormGroup,
    FormGroupDirective,
    FormRecord,
    NgForm,
} from '@angular/forms';
import type { ErrorStateMatcher as ESM } from '@angular/material/core';
import type { Observable } from 'rxjs';
import { map, merge } from 'rxjs';

// Base type for all form values
type AbstractFormValue<T> =
    T extends FormGroup<any>
        ? FormGroupValue<T>
        : T extends FormRecord<any>
          ? FormRecordValue<T>
          : T extends FormArray<any>
            ? FormArrayValue<T>
            : T extends FormControl<any>
              ? FormControlValue<T>
              : never;

// Extract values from FormGroup
type FormGroupValue<T> =
    T extends FormGroup<infer U>
        ? {
              [K in keyof U]: AbstractFormValue<U[K]>;
          }
        : never;

// Extract values from FormRecord
type FormRecordValue<T> =
    T extends FormRecord<infer U>
        ? {
              [K in keyof U]: AbstractFormValue<U[K]>;
          }
        : never;

// Extract values from FormArray
type FormArrayValue<T> =
    T extends FormArray<infer U> ? AbstractFormValue<U>[] : never;

// Extract values from FormControl
type FormControlValue<T> = T extends FormControl<infer U> ? U : never;

// Main type that combines all cases
type UnwrapFormValues<T> = AbstractFormValue<T>;

export type FormValues<T> = UnwrapFormValues<T>;

export interface ReactiveFormState<TForm extends FormGroup, TPayload> {
    readonly form: Signal<TForm>;
    validateAndBuildPayload(): Observable<TPayload>;
}

export function toObservableForm<T extends FormGroup>(
    control: T,
    ...otherObservables: Observable<unknown>[]
): Observable<T> {
    return merge(
        control.valueChanges,
        control.statusChanges,
        ...otherObservables
    ).pipe(map(() => control));
}

export function toSignalForm<T extends FormGroup>(
    control: T,
    ...otherObservables: Observable<unknown>[]
): Signal<T> {
    return toSignal(toObservableForm(control, ...otherObservables), {
        initialValue: control,
    });
}

/**
 * Custom error state matcher that checks if the control is invalid and dirty or touched or submitted.
 * If errorKeys is provided, it will also check if the parent has any of the error keys.
 */
export class ErrorStateMatcher implements ESM {
    private readonly onDirty: boolean;
    private readonly onTouched: boolean;
    private readonly errorKeys: string[];

    constructor(
        options: Partial<{
            onDirty: boolean;
            onTouched: boolean;
            errorKeys: string | string[];
        }> = {}
    ) {
        this.onDirty = options.onDirty ?? true;
        this.onTouched = options.onTouched ?? true;

        if (!options.errorKeys) {
            this.errorKeys = [];
        } else {
            this.errorKeys = Array.isArray(options.errorKeys)
                ? options.errorKeys
                : [options.errorKeys];
        }
    }

    isErrorState(
        control: AbstractControl | null,
        form: FormGroupDirective | NgForm | null
    ): boolean {
        const isSubmitted = !!form?.submitted;
        const controlInvalid =
            control?.invalid &&
            ((this.onDirty && control.dirty) ||
                (this.onTouched && control.touched) ||
                isSubmitted);

        if (!this.errorKeys.length) return !!controlInvalid;

        const parentHasError = this.errorKeys.some(
            (key) =>
                control?.parent?.hasError(key) &&
                (control.dirty || control.touched || isSubmitted)
        );

        return !!(controlInvalid || parentHasError);
    }
}
